import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';

const LocationHistory = ({ trailerId }) => {
  const [locationHistory, setLocationHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const sessionUser = useSelector(state => state.session.user);

  useEffect(() => {
    const fetchLocationHistory = async () => {
      try {
        const response = await fetch(`/api/trailers/${trailerId}/location-history`);

        if (!response.ok) {
          throw new Error(`Failed to fetch location history: ${response.status}`);
        }

        const data = await response.json();
        setLocationHistory(data);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching location history:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    if (trailerId) {
      fetchLocationHistory();
    }
  }, [trailerId]);

  // Format the ISO timestamp for display
  const formatTime = (isoTimestamp) => {
    if (!isoTimestamp) return '';
    const date = new Date(isoTimestamp);
    return date.toLocaleString();
  };

  // Format date only (for timeline display)
  const formatDateOnly = (isoTimestamp) => {
    if (!isoTimestamp) return '';
    const date = new Date(isoTimestamp);
    return date.toLocaleDateString();
  };

  if (loading) {
    return <div className="p-5 text-center text-xl text-gray-500 bg-gray-100 rounded-lg my-5">Loading location history...</div>;
  }

  if (error) {
    return <div className="p-5 text-center text-xl text-red-600 bg-red-50 border-l-4 border-red-600 rounded-lg my-5">Error: {error}</div>;
  }

  if (!locationHistory || locationHistory.length === 0) {
    return <div className="p-5 text-center text-xl text-gray-500 bg-gray-100 rounded-lg my-5 italic">No location history available</div>;
  }

  // Get the current (most recent) location record
  const currentLocation = locationHistory[0];
  const isCurrentActive = !currentLocation.exit_time;

  return (
    <div className="my-5 p-5 bg-gray-50 rounded-lg shadow-sm">
      {/* Current Location - Full width card with grid layout from option 2 */}
      <div className="mb-8 pb-5 border-b border-gray-200">
        <h3 className="text-3xl font-semibold text-gray-800 mb-4">Current Location</h3>
        <div className="bg-white p-6 rounded-lg border-l-4 border-blue-500 shadow-md">
          <p className="text-2xl font-bold text-gray-700 capitalize">{currentLocation.location}</p>
          {isCurrentActive && (
            <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <p className="text-lg text-gray-500">Time at location</p>
                <p className="text-xl font-medium text-green-600">
                  {currentLocation.current_duration_formatted || 'Just arrived'}
                </p>
              </div>
              <div>
                <p className="text-lg text-gray-500">Since</p>
                <p className="text-xl font-medium text-green-600">{formatTime(currentLocation.entry_time)}</p>
              </div>
              <div>
                <p className="text-lg text-gray-500">Changed by</p>
                <p className="text-xl font-medium text-green-600">{currentLocation.user_alpha_code}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Recent Location History - Horizontal Timeline */}
      <div className="mb-8">
        <h3 className="text-3xl font-semibold text-gray-800 mb-4">Recent Locations</h3>
        <div className="relative">
          {/* Horizontal line */}
          <div className="absolute h-1 bg-gray-200 top-8 left-0 right-0 z-0"></div>

          {/* Scrollable container */}
          <div className="overflow-x-auto pb-4">
            <div className="flex space-x-8 relative z-10 py-2" style={{ minWidth: 'max-content' }}>
              {locationHistory.slice(0, 5).map((record, index) => (
                <div key={record.id} className="flex flex-col items-center w-48">
                  {/* Timeline dot */}
                  <div className={`w-4 h-4 rounded-full mb-2 ${index === 0 ? 'bg-blue-500' : 'bg-gray-400'}`}></div>

                  <div className={`w-full p-4 bg-white rounded-lg shadow-sm
                    ${index === 0 ? 'border-blue-500 border' : ''}`}>
                    <p className="text-xl font-bold text-gray-700 capitalize text-center">{record.location}</p>
                    <p className="text-lg text-center text-gray-500 mt-1">
                      {formatDateOnly(record.entry_time)}
                    </p>
                    <p className="text-lg text-center text-gray-400 mt-1">
                      {record.user_alpha_code}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Full Location History */}
      <div>
        <h3 className="text-3xl font-semibold text-gray-800 mb-4">Complete History</h3>
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-lg font-medium text-gray-500 uppercase tracking-wider">Location</th>
                <th className="px-6 py-3 text-left text-lg font-medium text-gray-500 uppercase tracking-wider">Entry Time</th>
                <th className="px-6 py-3 text-left text-lg font-medium text-gray-500 uppercase tracking-wider">Exit Time</th>
                <th className="px-6 py-3 text-left text-lg font-medium text-gray-500 uppercase tracking-wider">Duration</th>
                <th className="px-6 py-3 text-left text-lg font-medium text-gray-500 uppercase tracking-wider">Changed By</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {locationHistory.map((record) => (
                <tr key={record.id} className={`hover:bg-gray-50 ${!record.exit_time ? 'bg-blue-50' : ''}`}>
                  <td className="px-6 py-4 whitespace-nowrap text-lg font-medium text-gray-900 capitalize">{record.location}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-lg text-gray-500">{formatTime(record.entry_time)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-lg text-gray-500">
                    {record.exit_time ? formatTime(record.exit_time) : 'Current'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-lg text-gray-500">
                    {record.exit_time ? record.duration_formatted : record.current_duration_formatted || 'Just arrived'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-lg text-gray-500">{record.user_alpha_code}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default LocationHistory;
